/**
 * Calculates how much of an element is in the vertical range of the viewport, and how close it
 * is, if it's outside of the viewport.
 * @param {Element|string} element - An element or query string to reference the target element
 * @return {Object}
 */
export function verticalVisibility(element) {
  const DOMElement = (typeof element === 'string') ? document.querySelector(element) : element;
  const rect = DOMElement.getBoundingClientRect();
  const viewport = { width: window.innerWidth, height: window.innerHeight };

  if (rect.top > viewport.height) {
    return {
      visibility: 0,
      state: 'BELOW_VIEW',
      distance: rect.top - viewport.height,
    };
  }

  if (rect.bottom <= 0) {
    return {
      visibility: 0,
      state: 'ABOVE_VIEW',
      distance: Math.abs(rect.bottom),
    };
  }

  if (rect.top >= 0 && rect.bottom <= viewport.height) {
    return {
      visibility: 1,
      state: 'IN_VERTICAL_VIEW',
      distance: 0,
    };
  }

  if (rect.top < 0 && rect.bottom > viewport.height) {
    return {
      visibility: viewport.height / rect.height,
      state: 'TOP_AND_BOTTOM_TRUNCATED',
      distance: 0,
    };
  }

  if (rect.top < 0 && rect.bottom <= viewport.height) {
    return {
      visibility: rect.bottom / rect.height,
      state: 'TOP_TRUNCATED',
      distance: 0,
    };
  }

  if (rect.top >= 0 && rect.bottom > viewport.height) {
    return {
      visibility: (viewport.height - rect.top) / rect.height,
      state: 'BOTTOM_TRUNCATED',
      distance: 0,
    };
  }

  return {
    visibility: 0,
    state: 'NOT_IN_VERTICAL_VIEW',
    distance: Infinity,
  };
}

/**
 * Calculates how much of an element is in the horizontal range of the viewport, and how close it
 * is, if it's outside of the viewport.
 * @param {Element|string} element - An element or query string to reference the target element
 * @return {Object}
 */
export function horizontalVisibility(element) {
  const DOMElement = (typeof element === 'string') ? document.querySelector(element) : element;
  const rect = DOMElement.getBoundingClientRect();
  const viewport = { width: window.innerWidth, height: window.innerHeight };

  if (rect.left > viewport.width) {
    return {
      visibility: 0,
      state: 'RIGHT_OF_VIEW',
      distance: rect.left - viewport.width,
    };
  }

  if (rect.right <= 0) {
    return {
      visibility: 0,
      state: 'LEFT_OF_VIEW',
      distance: Math.abs(rect.right),
    };
  }

  if (rect.left >= 0 && rect.right <= viewport.width) {
    return {
      visibility: 1,
      state: 'IN_HORIZONTAL_VIEW',
      distance: 0,
    };
  }

  if (rect.left < 0 && rect.right > viewport.width) {
    return {
      visibility: viewport.width / rect.width,
      state: 'LEFT_AND_RIGHT_TRUNCATED',
      distance: 0,
    };
  }

  if (rect.left < 0 && rect.right <= viewport.width) {
    return {
      visibility: rect.right / viewport.width,
      state: 'LEFT_TRUNCATED',
      distance: 0,
    };
  }

  if (rect.left >= 0 && rect.right > viewport.width) {
    return {
      visibility: (viewport.width - rect.left) / rect.width,
      state: 'RIGHT_TRUNCATED',
      distance: 0,
    };
  }

  return {
    visibility: 0,
    state: 'NOT_IN_HORIZONTAL_VIEW',
    distance: Infinity,
  };
}

/**
 * Calculates how much of an element is in the visible range of the viewport, and how close it
 * is, if it's outside of the viewport.
 * @param {Element|string} element - An element or query string to reference the target element
 * @return {Object}
 */
export default function elementVisibility(element) {
  const DOMElement = (typeof element === 'string') ? document.querySelector(element) : element;
  const vertical = verticalVisibility(DOMElement);
  const horizontal = horizontalVisibility(DOMElement);
  return {
    visibility: vertical.visibility * horizontal.visibility,
    state: [vertical.state, horizontal.state],
    distance: Math.max(vertical.distance, horizontal.distance),
  };
}
